import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronUp } from 'lucide-react';

export default function FAQ() {
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "What is BabyBookin?",
      answer: "BabyBookin is a digital baby book application that allows you to create, store, and share precious memories, photos, milestones, and stories about your child. It's like a traditional baby book, but digital, collaborative, and always accessible."
    },
    {
      question: "How do I create a baby book?",
      answer: "After signing up, you'll be guided through a simple setup process where you'll enter information about your baby, parents, pregnancy journey, and birth details. You can skip any sections and come back to them later."
    },
    {
      question: "Can I invite family members to view my baby book?",
      answer: "Yes! Go to Settings and click 'Invite User'. Enter their email address and choose their role (Viewer, Editor, or Admin). They'll receive an invitation and can accept it when they log in or create an account."
    },
    {
      question: "What are the different user roles?",
      answer: "Viewer: Can view all entries and photos. Editor: Can add new entries and milestones, edit their own entries, and view all content. Admin: Full access to all features, can edit any entry, and manage users."
    },
    {
      question: "How do I add journal entries?",
      answer: "From the Home or Journal view, click the '+' button to add a new entry. You can add text, photos, and optionally mark it as a milestone. Entries are automatically timestamped."
    },
    {
      question: "Can I add multiple photos to an entry?",
      answer: "Yes! You can upload multiple photos to any journal entry or milestone. There's also a bulk upload feature for adding many photos at once."
    },
    {
      question: "What is the bulk upload feature?",
      answer: "Bulk upload allows you to upload multiple photos at once and automatically create journal entries for each photo. This is perfect for importing photos from your camera roll or catching up on missed entries."
    },
    {
      question: "How do I record milestones?",
      answer: "Click the '+' button in the Milestones view or mark an entry as a milestone when creating a journal entry. Choose from common milestones like 'First Steps' or 'First Words', or create custom milestones."
    },
    {
      question: "Is my data secure?",
      answer: "Yes! Your data is stored securely using Google Firebase with industry-standard encryption. Only you and the family members you invite can access your baby book. We never sell or share your personal information."
    },
    {
      question: "Can I download or export my baby book?",
      answer: "Currently, you can view and access all your content online. We're working on adding export and print features in the future. All your photos and text are stored in your baby book and accessible anytime."
    },
    {
      question: "What happens if I delete my account?",
      answer: "If you delete your account, all your baby books and data will be permanently deleted within 30 days. Make sure to save any photos or content you want to keep before deleting your account."
    },
    {
      question: "Can I create multiple baby books?",
      answer: "Currently, each account supports one baby book. If you have multiple children, we recommend creating separate accounts or using Premium which supports up to 6 baby books."
    },
    {
      question: "How do I edit or delete an entry?",
      answer: "Admins can edit or delete any entry. Editors can edit or delete their own entries. Click on an entry to view options for editing or deleting."
    },
    {
      question: "What file types can I upload?",
      answer: "You can upload common image formats including JPG, JPEG, PNG, GIF, and WEBP. We recommend JPG or PNG for best quality and compatibility."
    },
    {
      question: "Is BabyBookin free?",
      answer: "BabyBookin offers a generous free tier with up to 3 baby books, 150 photos per book, and 1 contributor. Premium ($4.99/month) unlocks unlimited photos, more books, and more contributors. Print-to-book is available to all users - you just pay for each book you order."
    },
    {
      question: "Can I use BabyBookin offline?",
      answer: "BabyBookin works best with an internet connection. However, if you've installed it as a Progressive Web App (PWA) on your device, you can view previously loaded content offline. New entries require an internet connection."
    },
    {
      question: "How do I install BabyBookin as an app on my phone?",
      answer: "On iPhone: Open babybookin.com in Safari, tap the Share button, and select 'Add to Home Screen'. On Android: Open babybookin.com in Chrome, tap the menu, and select 'Add to Home screen' or 'Install app'."
    },
    {
      question: "I forgot my password. How do I reset it?",
      answer: "On the login page, click 'Forgot Password?' and enter your email. You'll receive a password reset link. If you signed up with Google, use the 'Sign in with Google' button instead."
    },
    {
      question: "Can I change my baby's information after setup?",
      answer: "Yes! Go to Settings and you can update any of your baby book information, including names, dates, and all the pre-birth and birth details."
    },
    {
      question: "Who can see my baby book?",
      answer: "Only you and the family members you explicitly invite can see your baby book. Your content is private and not visible to other BabyBookin users or the public."
    },
    {
      question: "How do I contact support?",
      answer: "For support, questions, or feedback, email us at support@babybookin.com. We typically respond within 24-48 hours."
    },
    {
      question: "What devices work with BabyBookin?",
      answer: "BabyBookin works on any device with a modern web browser, including iPhones, Android phones, tablets, and desktop computers. For the best mobile experience, install it as a PWA (see above)."
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-ocean-50 to-sand-50 p-8">
      <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg p-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-ocean-600 hover:text-ocean-700 mb-6"
        >
          <ArrowLeft size={20} />
          Back
        </button>

        <h1 className="text-3xl font-bold text-ocean-900 mb-6">Frequently Asked Questions</h1>
        <p className="text-gray-600 mb-8">
          Find answers to common questions about BabyBookin. If you can't find what you're looking for, 
          contact us at support@babybookin.com
        </p>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div key={index} className="border border-gray-200 rounded-lg">
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
              >
                <h3 className="text-lg font-semibold text-gray-800 pr-4">{faq.question}</h3>
                {openIndex === index ? (
                  <ChevronUp className="flex-shrink-0 text-ocean-600" size={20} />
                ) : (
                  <ChevronDown className="flex-shrink-0 text-ocean-600" size={20} />
                )}
              </button>
              
              {openIndex === index && (
                <div className="px-4 pb-4">
                  <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}